import { useEffect, useState } from "react";
import classNames from "classnames";
import { Typography } from "components/atoms/Typography";
import { DropDown } from "components/atoms/DropDown";
import { HorizontalProgressBar } from "../HorizontalProgressBar";
import { ArrowRight, Spinner } from "components/atoms/Icon";
import { FetchError } from "../FetchErrorMsg";
import "./index.scss";

interface IProps {
  isMobile?: boolean;
  isTab?: boolean;
  data: any;
  isLoading: any;
  isError: any;
  theme: "light" | "dark";
}

export const TestScoreByTests = ({
  isMobile,
  isTab,
  data,
  isLoading,
  isError,
  theme,
}: IProps) => {
  const [hasData, setHasData] = useState(false);

  const [selectedTest, setSelectedTest] = useState<
    null | string | { [key: number]: string }
  >(null);

  const [selectedSubject, setSelectedSubject] = useState<
    null | string | { [key: number]: string }
  >(null);

  const [selectedTopic, setSelectedTopic] = useState<
    null | string | { [key: number]: string }
  >(null);

  const [findCategory, setFindCategory] = useState<any>();

  const [toggleShowMore, setToggleShowMore] = useState(false);

  const [modifyData, setModifyData] = useState<any>();

  const handleToggleView = () => setToggleShowMore((prev) => !prev);

  const getColor = (color: string | null) => {
    switch (color) {
      case "RED":
        return "red";
      case "YELLOW":
        return "yellow";
      case "GREEN":
        return "green";
      case null:
        return "grey";
      default:
        return "grey";
    }
  };

  const matchColor = (percent: any, colors: any, subTopic: any) => {
    if (subTopic.Total === 0 && subTopic.Correct === 0) {
      return null;
    } else if (subTopic.Total > 0 && subTopic.Correct === 0) {
      return "RED";
    } else if (colors && Array.isArray(colors)) {
      const obj = colors.find(
        (col: any) => percent >= col.PercentMin && percent <= col.PercentMax
      );
      return obj ? obj.Color : null;
    } else {
      return null;
    }
  };

  useEffect(() => {
    setSelectedTopic(null);
    setHasData(false);
  }, [selectedSubject]);

  useEffect(() => {
    setSelectedSubject(null);
    setSelectedTopic(null);
    setHasData(false);
  }, [selectedTest]);

  useEffect(() => {
    if (
      data &&
      modifyData &&
      selectedTest !== null &&
      Object.values(selectedTest)[0] !== "All" &&
      selectedSubject !== null &&
      selectedTopic !== null
    ) {
      setFindCategory(
        modifyData.tests[Object.keys(selectedTest)[0]].Subjects[
          Object.keys(selectedSubject)[0]
        ].Topics[Object.keys(selectedTopic)[0]].SubTopics
      );
      setHasData(true);
    } else if (
      data &&
      modifyData &&
      selectedTest !== null &&
      Object.values(selectedTest)[0] === "All" &&
      selectedSubject !== null &&
      selectedTopic !== null
    ) {
      const allSubTopics = modifyData.tests
        .slice(1)
        .map((test: any) => test.Subjects[Object.keys(selectedSubject)[0]])
        .map((sub: any) => sub.Topics[Object.keys(selectedTopic)[0]]);

      const subTopicMap = new Map();

      allSubTopics.forEach((item: any) => {
        item.SubTopics.forEach((subTopic: any) => {
          if (subTopicMap.has(subTopic.SubTopicId)) {
            const existingSubTopic = subTopicMap.get(subTopic.SubTopicId);
            existingSubTopic.Correct += subTopic.Correct;
            existingSubTopic.Total += subTopic.Total;

            existingSubTopic.Percentage =
              existingSubTopic.Total === 0
                ? 0
                : Math.floor(
                    (existingSubTopic.Correct / existingSubTopic.Total) * 100
                  );

            existingSubTopic.Color = matchColor(
              existingSubTopic.Percentage,
              modifyData.colors,
              existingSubTopic
            );
          } else {
            subTopicMap.set(subTopic.SubTopicId, { ...subTopic });
          }
        });
      });

      const mergeSubjects = Array.from(subTopicMap.values());

      setFindCategory(mergeSubjects);
      setHasData(true);
    }
  }, [selectedTopic]);

  const findSubjects = (data: any, modifyData: any) => {
    if (data && modifyData && modifyData.tests) {
      if (selectedTest === null) {
        return modifyData.tests[1].Subjects.map(
          (value: any) => value.SubjectName
        );
      } else if (Object.values(selectedTest)[0] === "All") {
        return modifyData.tests[1].Subjects.map(
          (value: any) => value.SubjectName
        );
      } else if (Object.keys(selectedTest)[0]) {
        return modifyData.tests[Object.keys(selectedTest)[0]].Subjects.map(
          (value: any) => value.SubjectName
        );
      } else {
        return [];
      }
    }
  };

  const findTopic = (data: any, modifyData: any) => {
    if (data && modifyData && modifyData.tests) {
      if (selectedTest === null || selectedSubject === null) {
        return modifyData.tests[1].Subjects[0].Topics.map(
          (value: any) => value.TopicName
        );
      } else if (Object.values(selectedTest)[0] === "All") {
        return modifyData.tests[1].Subjects[
          Object.keys(selectedSubject)[0]
        ].Topics.map((value: any) => value.TopicName);
      } else if (Object.keys(selectedTest)[0]) {
        return modifyData.tests[Object.keys(selectedTest)[0]].Subjects[
          Object.keys(selectedSubject)[0]
        ].Topics.map((value: any) => value.TopicName);
      } else {
        return [];
      }
    }
  };

  useEffect(() => {
    if (
      data &&
      data.tests &&
      Array.isArray(data.tests) &&
      data.tests.length > 0
    ) {
      setModifyData({ ...data, tests: [{ TestName: "All" }, ...data.tests] });
    }
  }, [data]);

  return (
    <div className={classNames("perform-by-test-wrapper")}>
      <Typography variant="h7Bold" label="View your performance by test(s):" />
      <div className={classNames("perform-by-test-filter-wrapper")}>
        {isLoading ? (
          <Spinner theme={theme} />
        ) : isError ? (
          <FetchError content="Oops! failed to load resources." />
        ) : (
          <>
            <div className={classNames("perform-by-test-form-wrapper")}>
              <div className="first-dropdown">
                <Typography variant="h8Bold" label="Tests:" />
                <DropDown
                  data={
                    data &&
                    modifyData &&
                    modifyData.tests &&
                    modifyData.tests.map((value: any) => value.TestName)
                  }
                  setDropDownSelection={setSelectedTest}
                  defaultSelected={selectedTest}
                  placeholder="Select test"
                />
              </div>

              <div className="second-dropdown">
                <Typography variant="h8Bold" label="Subject:" />
                <DropDown
                  data={findSubjects(data, modifyData)}
                  setDropDownSelection={setSelectedSubject}
                  defaultSelected={selectedSubject}
                  placeholder="Select subject"
                />
              </div>

              <div className="third-dropdown">
                <Typography variant="h8Bold" label="Topic:" />
                <DropDown
                  data={findTopic(data, modifyData)}
                  setDropDownSelection={setSelectedTopic}
                  defaultSelected={selectedTopic}
                  placeholder="Select topic"
                />
              </div>
            </div>
            <div className={classNames("perform-by-test-graph-wrapper")}>
              {hasData ? (
                <>
                  {findCategory.length === 0 ? (
                    <Typography
                      variant="h8Book"
                      label="Sub-Topics not available for selected Topic"
                      styles="perform-by-test-no-data"
                    />
                  ) : findCategory.length > 5 && !toggleShowMore ? (
                    findCategory
                      .slice(0, 5)
                      .map((category: any, index: number) => (
                        <div
                          key={index}
                          className={classNames(
                            "perform-by-test-progress-item"
                          )}
                        >
                          {isMobile ? null : (
                            <div
                              className={classNames(
                                "perform-by-test-progress-item-title"
                              )}
                            >
                              <Typography
                                variant="h8Med"
                                label={category.SubTopicName}
                              />
                            </div>
                          )}

                          <HorizontalProgressBar
                            complete={category.Correct}
                            total={category.Total}
                            percentage={category.Percentage}
                            isShowPercentageOnTop={false}
                            isShowProgressCount={false}
                            progressColor={getColor(
                              category.Total === 0 && category.Correct === 0
                                ? null
                                : category.Total > 0 && category.Correct === 0
                                  ? "RED"
                                  : category.Color
                            )}
                            styles={
                              category.Correct === 0 ? "" :
                              category.Total > 0 && category.Correct === 0
                                ? "perform-by-test-outer-bar-red"
                                : ""
                            }
                            label={`${category.Percentage}% -  
                          ${category.Correct} out of ${category.Total} Correct`}
                            isMobile={isMobile}
                            topic={category.SubTopicName}
                          />
                        </div>
                      ))
                  ) : (
                    findCategory.map((category: any, index: number) => (
                      <div
                        key={index}
                        className={classNames("perform-by-test-progress-item")}
                      >
                        {isMobile ? null : (
                          <div
                            className={classNames(
                              "perform-by-test-progress-item-title"
                            )}
                          >
                            <Typography
                              variant="h8Med"
                              label={category.SubTopicName}
                            />
                          </div>
                        )}

                        <HorizontalProgressBar
                          complete={category.Correct}
                          total={category.Total}
                          percentage={category.Percentage}
                          isShowPercentageOnTop={false}
                          isShowProgressCount={false}
                          progressColor={getColor(
                            category.Total === 0 && category.Correct === 0
                              ? null
                              : category.Total > 0 && category.Correct === 0
                                ? "RED"
                                : category.Color
                          )}
                          styles={
                            category.Correct === 0 ? "" :
                            category.Total > 0 && category.Correct === 0
                              ? "perform-by-test-outer-bar-red"
                              : ""
                          }
                          label={`${category.Percentage}% -  
                          ${category.Correct} out of ${category.Total} Correct`}
                          isMobile={isMobile}
                          topic={category.SubTopicName}
                        />
                      </div>
                    ))
                  )}
                  {findCategory.length > 5 ? (
                    <div
                      className={classNames(
                        "perform-by-test-toggle-view-wrapper"
                      )}
                    >
                      <div
                        className={classNames("toggle-show-text-icon-wrapper")}
                        onClick={handleToggleView}
                      >
                        <Typography
                          variant="h9Medium"
                          label={!toggleShowMore ? "Show more" : "Show less"}
                        />
                        <ArrowRight
                          color={theme === "light" ? "#000" : "#C0C0C0"}
                        />
                      </div>
                    </div>
                  ) : null}
                </>
              ) : (
                <Typography
                  variant="h8Book"
                  label="Apply a filter to view your performance on this section"
                  styles="perform-by-test-no-data"
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
